import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, Clock, CheckCircle, XCircle, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const FIELD_OPTIONS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'middle_name', label: 'Middle Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'emergency_contact', label: 'Emergency Contact' },
  { value: 'department', label: 'Department' },
  { value: 'designation', label: 'Designation' },
  { value: 'category', label: 'Category (Teaching / Non-teaching)' },
  { value: 'date_of_joining', label: 'Date of Joining' },
  { value: 'date_of_birth', label: 'Date of Birth' },
  { value: 'gender', label: 'Gender' },
  { value: 'qualification', label: 'Qualification' },
  { value: 'experience_years', label: 'Experience (years)' },
  { value: 'current_address', label: 'Current Address' },
  { value: 'permanent_address', label: 'Permanent Address' },
  { value: 'blood_group', label: 'Blood Group' },
  { value: 'marital_status', label: 'Marital Status' },
];

function statusBadge(s) {
  if (s === 'pending') return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  if (s === 'approved') return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
  if (s === 'rejected') return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
  return <Badge variant="outline">{s}</Badge>;
}

export function ProfileCorrections() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ field_name: '', proposed_value: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const h = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('hrms-token')}`,
  });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/me/corrections`, { headers: h(), credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.field_name || !form.proposed_value || !form.reason || form.reason.trim().length < 5) {
      toast.error('Please select a field, enter the correct value, and provide a reason (min 5 chars).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/employee-corrections`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({
          field_name: form.field_name,
          proposed_value: form.proposed_value,
          reason: form.reason.trim(),
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submit failed');
      toast.success(data.message || 'Correction request submitted.');
      setOpen(false);
      setForm({ field_name: '', proposed_value: '', reason: '' });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (id) => {
    if (!window.confirm('Withdraw this correction request?')) return;
    try {
      const res = await fetch(`${API_URL}/api/employee-corrections/${id}`, {
        method: 'DELETE', headers: h(), credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      toast.success('Request withdrawn');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="profile-corrections-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            Profile Correction Requests
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Spotted wrong info (e.g. department, designation, name)? Request a correction below — HR will review.</p>
        </div>
        <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={() => setOpen(true)} data-testid="request-correction-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" /> Request Correction
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">No correction requests yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map(it => (
              <div key={it.correction_id} className="flex items-start justify-between p-3 rounded-lg border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium capitalize">{(it.field_name || '').replace(/_/g, ' ')}</span>
                    {statusBadge(it.status)}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300 break-words">
                    <span className="text-slate-400">From:</span> {String(it.current_value ?? '(empty)')} → <span className="font-medium">{String(it.proposed_value ?? '')}</span>
                  </p>
                  {it.reason && <p className="text-xs text-slate-500 mt-1">Reason: {it.reason}</p>}
                  {it.reviewer_notes && <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">HR: {it.reviewer_notes}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(it.submitted_at).toLocaleString('en-IN')}</p>
                </div>
                {it.status === 'pending' && (
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => withdraw(it.correction_id)} data-testid={`withdraw-correction-${it.correction_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Profile Correction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Field to correct</Label>
              <Select value={form.field_name} onValueChange={(v) => setForm({ ...form, field_name: v })}>
                <SelectTrigger data-testid="correction-field-select"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent>
                  {FIELD_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Correct value</Label>
              <Input value={form.proposed_value} onChange={(e) => setForm({ ...form, proposed_value: e.target.value })} data-testid="correction-value-input" placeholder="Enter the correct value" />
            </div>
            <div>
              <Label>Why is this change needed?</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="correction-reason-input" placeholder="e.g. My department changed in Jan 2026 from CSE to AI&DS" rows={3} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={submit} disabled={submitting} data-testid="correction-submit-btn">
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
